import { useEffect, useState } from 'react'
import { Icon } from '../../components/icons/Icon'
import { InlineAlert } from '../../components/InlineAlert'
import { StatusPill } from '../../components/StatusPill'
import { acceptProposal, generateProposal, generateSummary, listProposals, listSummaries, rejectProposal } from '../../api/ai'
import { ApiError } from '../../api/problem'
import { label } from '../../lib/labels'
import type { AiProposalView, AiProposalStatus, AiSummaryView } from '../../types/ai'
import type { QualifiedSpendLineView } from '../../types/film'

export interface AiAssistPanelProps {
  productionId: string
  productionTitle: string
  spendLines: QualifiedSpendLineView[]
  /** Called after an Assessor proposal is accepted — the caller should re-fetch spend lines so the
   * Qualified spend grid reflects the fresh, server-computed `qualifyingPct` (this panel never
   * applies the accepted value optimistically). */
  onAccepted?: () => void
}

const FIELD_NAME = 'qualifying_pct'
const TARGET_TYPE_SPEND_LINE = 'QualifiedSpendLine'
const TARGET_TYPE_PRODUCTION = 'Production'

function statusVariant(status: AiProposalStatus): 'info' | 'success' | 'danger' {
  if (status === 'accepted') return 'success'
  if (status === 'rejected') return 'danger'
  return 'info'
}

function statusLabel(status: AiProposalStatus): string {
  if (status === 'accepted') return 'Accepted'
  if (status === 'rejected') return 'Rejected'
  return 'Pending review'
}

function formatPct(value: number | null): string {
  return value !== null && value !== undefined ? `${value}%` : 'Not yet assessed'
}

/**
 * The IB-172 "AI Assist" panel (M16 AI Engines, bounded demo slice): a read-only generated
 * narrative (Summary Generator, FR-M16.2) plus propose-only Assessor suggestions (FR-M16.3/
 * FR-M16.4) with real Accept/Reject CTAs. Every action here hits a real `/api/v1/ai-*` endpoint —
 * no mocked data, no dead buttons. The guarded write on accept happens entirely server-side
 * (`module.film`'s `SpendAdjustmentService` via `module.ai`'s `AiProposalService`); this component
 * never constructs a `qualifyingPct` request body itself (mirrors `api/film.ts`'s own locked-down
 * guarded-write contract).
 */
export function AiAssistPanel({ productionId, productionTitle, spendLines, onAccepted }: AiAssistPanelProps) {
  const [summary, setSummary] = useState<AiSummaryView | null>(null)
  const [summaryLoading, setSummaryLoading] = useState(false)
  const [summaryError, setSummaryError] = useState<string | null>(null)

  const [proposalsByLine, setProposalsByLine] = useState<Record<string, AiProposalView>>({})
  const [generatingLineId, setGeneratingLineId] = useState<string | null>(null)
  const [proposalError, setProposalError] = useState<string | null>(null)

  const [decidingProposalId, setDecidingProposalId] = useState<string | null>(null)
  const [decideAction, setDecideAction] = useState<'accept' | 'reject' | null>(null)
  const [comment, setComment] = useState('')
  const [deciding, setDeciding] = useState(false)
  const [decideError, setDecideError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    listSummaries(TARGET_TYPE_PRODUCTION, productionId)
      .then((rows) => {
        const latest = rows[0] ?? null
        if (!cancelled && latest) setSummary(latest)
      })
      .catch(() => undefined)

    Promise.all(spendLines.map((line) => listProposals(line.id).then((rows) => [line.id, rows[0] ?? null] as const)))
      .then((pairs) => {
        if (cancelled) return
        const next: Record<string, AiProposalView> = {}
        for (const [lineId, proposal] of pairs) {
          if (proposal) next[lineId] = proposal
        }
        setProposalsByLine(next)
      })
      .catch(() => undefined)

    return () => {
      cancelled = true
    }
    // spendLines identity changes each parent render; re-keying on productionId + length is
    // sufficient for this demo panel's read (a stale extra/missing row self-heals on next load).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productionId, spendLines.length])

  async function handleGenerateSummary() {
    setSummaryLoading(true)
    setSummaryError(null)
    try {
      const generated = await generateSummary(TARGET_TYPE_PRODUCTION, productionId)
      setSummary(generated)
    } catch (err) {
      setSummaryError(err instanceof ApiError ? (err.problem.detail ?? err.message) : 'Could not generate a summary.')
    } finally {
      setSummaryLoading(false)
    }
  }

  async function handleGenerateProposal(lineId: string) {
    setGeneratingLineId(lineId)
    setProposalError(null)
    try {
      const proposal = await generateProposal(TARGET_TYPE_SPEND_LINE, lineId, FIELD_NAME)
      setProposalsByLine((prev) => ({ ...prev, [lineId]: proposal }))
    } catch (err) {
      setProposalError(err instanceof ApiError ? (err.problem.detail ?? err.message) : 'Could not generate a proposal.')
    } finally {
      setGeneratingLineId(null)
    }
  }

  function openDecide(proposalId: string, action: 'accept' | 'reject') {
    setDecidingProposalId(proposalId)
    setDecideAction(action)
    setComment('')
    setDecideError(null)
  }

  function closeDecide() {
    setDecidingProposalId(null)
    setDecideAction(null)
    setComment('')
    setDecideError(null)
  }

  async function confirmDecide(lineId: string) {
    if (!decidingProposalId || !decideAction) return
    setDeciding(true)
    setDecideError(null)
    try {
      const updated =
        decideAction === 'accept'
          ? await acceptProposal(decidingProposalId, comment)
          : await rejectProposal(decidingProposalId, comment)
      setProposalsByLine((prev) => ({ ...prev, [lineId]: updated }))
      closeDecide()
      if (decideAction === 'accept') onAccepted?.()
    } catch (err) {
      setDecideError(err instanceof ApiError ? (err.problem.detail ?? err.message) : 'Could not record that decision.')
    } finally {
      setDeciding(false)
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-4)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--iw-space-2)' }}>
        <span style={{ color: 'var(--iw-accent)' }}>
          <Icon name="sparkles" size={18} />
        </span>
        <h2 style={{ margin: 0, fontSize: 'var(--iw-size-md)' }}>AI Assist</h2>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-2)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--iw-space-2)' }}>
          <h3 style={{ margin: 0, fontSize: 'var(--iw-size-sm)', color: 'var(--iw-text-muted)' }}>
            Case summary
          </h3>
          <span style={{ flex: 1 }} />
          <button type="button" className="iw-btn iw-btn--sm" onClick={() => void handleGenerateSummary()} disabled={summaryLoading}>
            {summaryLoading ? 'Generating…' : summary ? 'Regenerate summary' : 'Generate summary'}
          </button>
        </div>
        {summaryError ? <InlineAlert severity="error">{summaryError}</InlineAlert> : null}
        {summary ? (
          <div
            style={{
              padding: 'var(--iw-space-3)',
              background: 'var(--iw-surface-subtle)',
              border: '1px solid var(--iw-border)',
              borderRadius: 'var(--iw-radius-md)',
              whiteSpace: 'pre-wrap',
              fontSize: 'var(--iw-size-sm)',
            }}
          >
            {summary.content}
          </div>
        ) : (
          <p style={{ color: 'var(--iw-text-muted)', margin: 0, fontSize: 'var(--iw-size-sm)' }}>
            No AI summary has been generated for {productionTitle} yet.
          </p>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-2)' }}>
        <h3 style={{ margin: 0, fontSize: 'var(--iw-size-sm)', color: 'var(--iw-text-muted)' }}>
          Assessor suggestions — qualifying %
        </h3>
        {proposalError ? <InlineAlert severity="error">{proposalError}</InlineAlert> : null}
        {spendLines.length === 0 ? (
          <p style={{ color: 'var(--iw-text-muted)', margin: 0, fontSize: 'var(--iw-size-sm)' }}>
            No qualified spend lines recorded yet.
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-3)' }}>
            {spendLines.map((line) => {
              const proposal = proposalsByLine[line.id]
              return (
                <div
                  key={line.id}
                  style={{
                    padding: 'var(--iw-space-3)',
                    border: '1px solid var(--iw-border)',
                    borderRadius: 'var(--iw-radius-md)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--iw-space-2)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--iw-space-2)', flexWrap: 'wrap' }}>
                    <strong style={{ fontSize: 'var(--iw-size-sm)' }}>{label('spendCategory', line.category)}</strong>
                    <span style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)' }}>
                      {line.currency} {line.amount.toLocaleString()}
                    </span>
                    <span style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)' }}>
                      Current: {formatPct(line.qualifyingPct)}
                    </span>
                    <span style={{ flex: 1 }} />
                    {!proposal || proposal.status !== 'pending' ? (
                      <button
                        type="button"
                        className="iw-btn iw-btn--sm"
                        onClick={() => void handleGenerateProposal(line.id)}
                        disabled={generatingLineId === line.id}
                      >
                        {generatingLineId === line.id ? 'Generating…' : proposal ? 'Generate new proposal' : 'Generate AI proposal'}
                      </button>
                    ) : null}
                  </div>

                  {proposal ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-2)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--iw-space-2)', flexWrap: 'wrap' }}>
                        <StatusPill variant={statusVariant(proposal.status)} label={statusLabel(proposal.status)} />
                        <span style={{ fontSize: 'var(--iw-size-sm)' }}>
                          Suggested: <strong>{proposal.proposedValue}%</strong>
                        </span>
                        {proposal.confidence !== null ? (
                          <span style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)' }}>
                            confidence {proposal.confidence}%
                          </span>
                        ) : null}
                      </div>
                      <p style={{ margin: 0, fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>
                        {proposal.rationale}
                      </p>

                      {proposal.status === 'pending' ? (
                        decidingProposalId === proposal.id ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-2)' }}>
                            {decideError ? <InlineAlert severity="error">{decideError}</InlineAlert> : null}
                            <textarea
                              className="iw-input"
                              rows={2}
                              placeholder={
                                decideAction === 'accept'
                                  ? 'Optional comment to accompany the accepted adjustment…'
                                  : 'Optional comment on why this suggestion is being rejected…'
                              }
                              value={comment}
                              onChange={(e) => setComment(e.target.value)}
                            />
                            <div style={{ display: 'flex', gap: 'var(--iw-space-2)' }}>
                              <button
                                type="button"
                                className="iw-btn iw-btn--sm iw-btn--primary"
                                onClick={() => void confirmDecide(line.id)}
                                disabled={deciding}
                              >
                                {deciding ? 'Saving…' : decideAction === 'accept' ? 'Confirm accept' : 'Confirm reject'}
                              </button>
                              <button type="button" className="iw-btn iw-btn--sm" onClick={closeDecide} disabled={deciding}>
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', gap: 'var(--iw-space-2)' }}>
                            <button
                              type="button"
                              className="iw-btn iw-btn--sm iw-btn--primary"
                              onClick={() => openDecide(proposal.id, 'accept')}
                            >
                              <Icon name="check" size={14} /> Accept
                            </button>
                            <button type="button" className="iw-btn iw-btn--sm" onClick={() => openDecide(proposal.id, 'reject')}>
                              <Icon name="x" size={14} /> Reject
                            </button>
                          </div>
                        )
                      ) : null}
                    </div>
                  ) : (
                    <p style={{ margin: 0, fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>
                      No AI suggestion generated for this line yet.
                    </p>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
